import { create } from 'zustand';
import type { MeResult, RoleInfo } from './types';
import { hasPermission } from '../../lib/permissions';
import type { AuthStatus } from './sessionPhase';

export { hasPermission };
export type { AuthStatus };

// localStorage keys (distinct from legacy CRM keys)
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'hw_access',
  REFRESH_TOKEN: 'hw_refresh',
  DEVICE_ID: 'hw_device_id',
} as const;

// Generate a stable device_id on first use.
function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(STORAGE_KEYS.DEVICE_ID, id);
  return id;
}

export interface SessionTokens {
  accessToken: string;
  refreshToken: string;
}

interface AuthV2State {
  accessToken: string | null;
  refreshToken: string | null;
  user: MeResult | null;
  roles: RoleInfo[];
  permissions: string[];
  status: AuthStatus;
  deviceId: string;

  // Actions
  setSession: (tokens: SessionTokens, me?: MeResult) => void;
  updateTokens: (tokens: SessionTokens) => void;
  setMe: (me: MeResult) => void;
  /** /me failed for a non-auth reason — enter the shell rather than hang. */
  acceptDegradedSession: () => void;
  logout: () => void;
  hydrateFromStorage: () => void;
}

export const useAuthV2Store = create<AuthV2State>((set) => ({
  accessToken: null,
  refreshToken: null,
  user: null,
  roles: [],
  permissions: [],
  status: 'idle',
  deviceId: getOrCreateDeviceId(),

  setSession(tokens, me) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
    set({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      user: me ?? null,
      roles: me?.roles ?? [],
      permissions: me?.permissions ?? [],
      // Tokens without /me are not a ready session — the sidebar is gated
      // on permissions from /me, so stay on 'loading' until setMe.
      status: me ? 'authenticated' : 'loading',
    });
  },

  updateTokens(tokens) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
    set({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    });
  },

  setMe(me) {
    set({
      user: me,
      roles: me.roles ?? [],
      permissions: me.permissions ?? [],
      status: 'authenticated',
    });
  },

  acceptDegradedSession() {
    set((s) => (s.accessToken ? { status: 'authenticated' as const } : {}));
  },

  logout() {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    set({
      accessToken: null,
      refreshToken: null,
      user: null,
      roles: [],
      permissions: [],
      status: 'unauthenticated',
    });
  },

  hydrateFromStorage() {
    const access = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const refresh = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (access && refresh) {
      set({
        accessToken: access,
        refreshToken: refresh,
        status: 'loading',
      });
    } else {
      set({ status: 'unauthenticated' });
    }
  },
}));

// Selector helpers

export function useIsAuthenticated() {
  return useAuthV2Store((s) => s.status === 'authenticated' && s.accessToken !== null);
}

/**
 * Check if the user holds a specific permission. Nav and in-page gates
 * share this helper; the server still enforces the same codes.
 */
export function usePermission(resource: string, action: string, schoolId?: string): boolean {
  const permissions = useAuthV2Store((s) => s.permissions);
  return hasPermission(permissions, resource, action, schoolId);
}
